import { useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import Table, { TableControls, TableHeading, buildActionColumn } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import TextField from "@/components/TextField";
import Tag from "@/components/Tag";
import { actionId } from "@/hooks/useTableRowMenu";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { RosterActivityEvent } from "@/features/roster-activity";
import { COHORT_LABELS, KIND_LABELS } from "@/features/roster-activity";
import CopyActions from "./CopyActions";
import {
  FacilityColumnHeader,
  FirstNameColumnHeader,
  LastNameColumnHeader,
  PlainColumnHeader,
  ROSTER_HEADER_META,
  RosterResultsHeaderProvider,
  SortColumnHeader
} from "./RosterResultsColumnHeader";
import {
  filterRosterActivityEvents,
  hasActiveRosterResultsFilters,
  sortRosterEventsFromSortingState,
  type RosterResultsFilters
} from "../rosterEventFields";

type Props = {
  events: RosterActivityEvent[];
  showCopyActions?: boolean;
};

const COHORT_TAG_TYPE = {
  MEMBER: "tertiary",
  ASSOCIATE: "information",
  STUDENT: "secondary"
} as const;

type SideSheetTarget = {
  pgaId: string;
  linkPath?: Path | null;
  personName: string;
};

const EMPTY_FILTERS: RosterResultsFilters = {
  firstName: "",
  lastName: "",
  facility: ""
};

const DEFAULT_SORTING: SortingState = [{ id: "occurredAt", desc: true }];

const SORTABLE_HEADER_META = {
  ...ROSTER_HEADER_META,
  sortType: "text" as const
};

function EmptyRosterActivity() {
  return (
    <EmptyState
      iconName="event_busy"
      title="No Roster Activity"
      caption="Try another week, month, or year, or adjust member type and change type filters."
    />
  );
}

function FilteredEmptyState({ useColumnHeaderFilters }: { useColumnHeaderFilters: boolean }) {
  return (
    <EmptyState
      iconName="search_off"
      title="No Results Found"
      caption={
        useColumnHeaderFilters
          ? "Try adjusting the name or facility filters in the column headers."
          : "Try adjusting the name or facility filters above the table."
      }
    />
  );
}

function buildEventColumns(
  onViewPerson: (event: RosterActivityEvent) => void,
  sorting: SortingState,
  onToggleSort: (columnId: string) => void,
  showHeaderFilters: boolean
): ColumnDef<RosterActivityEvent, unknown>[] {
  return [
    {
      accessorKey: "kind",
      header: () => (
        <SortColumnHeader label="Change Type" columnId="kind" sorting={sorting} onToggleSort={onToggleSort} />
      ),
      enableSorting: false,
      size: 160,
      meta: { ...SORTABLE_HEADER_META, sortLabel: "Change Type" },
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface whitespace-nowrap">
          {KIND_LABELS[row.original.kind]}
        </span>
      )
    },
    {
      accessorKey: "occurredAt",
      header: () => (
        <SortColumnHeader label="Date" columnId="occurredAt" sorting={sorting} onToggleSort={onToggleSort} />
      ),
      enableSorting: false,
      size: 140,
      meta: { ...SORTABLE_HEADER_META, sortType: "date", sortLabel: "Date" },
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant whitespace-nowrap">
          {DateTime.fromISO(row.original.occurredAt).toFormat("MMM d, yyyy")}
        </span>
      )
    },
    {
      accessorKey: "firstName",
      header: showHeaderFilters
        ? FirstNameColumnHeader
        : () => (
            <SortColumnHeader label="First Name" columnId="firstName" sorting={sorting} onToggleSort={onToggleSort} />
          ),
      enableSorting: false,
      size: 160,
      meta: { ...SORTABLE_HEADER_META, sortLabel: "First Name" },
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface">{row.original.firstName ?? ""}</span>
      )
    },
    {
      accessorKey: "lastName",
      header: showHeaderFilters
        ? LastNameColumnHeader
        : () => (
            <SortColumnHeader label="Last Name" columnId="lastName" sorting={sorting} onToggleSort={onToggleSort} />
          ),
      enableSorting: false,
      size: 160,
      meta: { ...SORTABLE_HEADER_META, sortLabel: "Last Name" },
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface">{row.original.lastName ?? ""}</span>
      )
    },
    {
      accessorKey: "facilityName",
      header: showHeaderFilters
        ? FacilityColumnHeader
        : () => (
            <SortColumnHeader label="Facility" columnId="facilityName" sorting={sorting} onToggleSort={onToggleSort} />
          ),
      enableSorting: false,
      size: 200,
      meta: { ...SORTABLE_HEADER_META, sortLabel: "Facility" },
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant">{row.original.facilityName ?? ""}</span>
      )
    },
    {
      accessorKey: "headline",
      header: () => <PlainColumnHeader label="Activity" />,
      enableSorting: false,
      size: 240,
      meta: ROSTER_HEADER_META,
      cell: ({ row }) => <span className="text-body-medium text-sys-brand-on-surface">{row.original.headline}</span>
    },
    {
      accessorKey: "detail",
      header: () => <PlainColumnHeader label="Detail" />,
      enableSorting: false,
      size: 240,
      meta: ROSTER_HEADER_META,
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant">{row.original.detail?.trim() ?? ""}</span>
      )
    },
    {
      accessorKey: "cohort",
      header: () => (
        <SortColumnHeader label="Program Type" columnId="cohort" sorting={sorting} onToggleSort={onToggleSort} />
      ),
      enableSorting: false,
      size: 132,
      minSize: 120,
      meta: { ...SORTABLE_HEADER_META, sortLabel: "Program Type" },
      cell: ({ row }) => (
        <Tag tagType={COHORT_TAG_TYPE[row.original.cohort]} size="small" tagStyle="standard">
          {COHORT_LABELS[row.original.cohort]}
        </Tag>
      )
    },
    buildActionColumn<RosterActivityEvent>({
      getActions: () => [],
      setMenu: () => undefined,
      actionId,
      showViewButton: (event) => Boolean(event.pgaId),
      onViewClick: onViewPerson,
      viewButtonTitle: "View person details",
      isRowNavigable: (event) => Boolean(event.pgaId && event.linkPath)
    })
  ];
}

export default function EventGroups({ events, showCopyActions = true }: Props) {
  const navigate = useNavigate();
  const { isCompactLayout } = useIsCompactLayout();
  const [sideSheet, setSideSheet] = useState<SideSheetTarget | null>(null);
  const [filters, setFilters] = useState<RosterResultsFilters>(EMPTY_FILTERS);
  const [openHeaderFilters, setOpenHeaderFilters] = useState<Partial<Record<keyof RosterResultsFilters, boolean>>>({});
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const onViewPerson = useCallback((event: RosterActivityEvent) => {
    if (!event.pgaId) return;
    setSideSheet({
      pgaId: event.pgaId,
      linkPath: event.linkPath,
      personName: event.personName
    });
  }, []);

  const onFilterChange = useCallback((key: keyof RosterResultsFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const onFilterOpenChange = useCallback((key: keyof RosterResultsFilters, open: boolean) => {
    setOpenHeaderFilters((current) => ({ ...current, [key]: open }));
  }, []);

  const onToggleSort = useCallback((columnId: string) => {
    setSorting((current) => {
      const active = current[0];
      if (active?.id === columnId) {
        return [{ id: columnId, desc: !active.desc }];
      }

      return [{ id: columnId, desc: columnId === "occurredAt" }];
    });
  }, []);

  const showHeaderFilters = !isCompactLayout;

  const columns = useMemo(
    () => buildEventColumns(onViewPerson, sorting, onToggleSort, showHeaderFilters),
    [onToggleSort, onViewPerson, showHeaderFilters, sorting]
  );
  const filteredEvents = useMemo(() => filterRosterActivityEvents(events, filters), [events, filters]);
  const displayEvents = useMemo(
    () => sortRosterEventsFromSortingState(filteredEvents, sorting),
    [filteredEvents, sorting]
  );
  const activeFilters = hasActiveRosterResultsFilters(filters);
  const showEmptyTable = events.length === 0;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setOpenHeaderFilters({});
  };

  const headerContextValue = useMemo(
    () => ({
      filters,
      openHeaderFilters,
      sorting,
      onToggleSort,
      onFilterChange,
      onFilterOpenChange
    }),
    [filters, onFilterChange, onFilterOpenChange, onToggleSort, openHeaderFilters, sorting]
  );

  const tableFilters = (
    <>
      <TextField
        label="First Name"
        name="roster-filter-first-name"
        value={filters.firstName}
        onChangeText={(value) => onFilterChange("firstName", value)}
        leadingIcon="search"
        className="w-full"
      />
      <TextField
        label="Last Name"
        name="roster-filter-last-name"
        value={filters.lastName}
        onChangeText={(value) => onFilterChange("lastName", value)}
        leadingIcon="search"
        className="w-full"
      />
      <TextField
        label="Facility"
        name="roster-filter-facility"
        value={filters.facility}
        onChangeText={(value) => onFilterChange("facility", value)}
        leadingIcon="search"
        className="w-full"
      />
    </>
  );

  return (
    <div data-testid="roster-activity-event-groups">
      <TableHeading
        heading={`Results (${displayEvents.length})`}
        body="All roster changes, retirements, and in memoriam entries for the selected filters."
      />

      {showCopyActions ? (
        <div className="mb-4">
          <CopyActions events={displayEvents} />
        </div>
      ) : null}

      <RosterResultsHeaderProvider value={headerContextValue}>
        <Table
          data={displayEvents}
          loading={false}
          columns={columns}
          sortingState={sorting}
          onSortingChange={setSorting}
          serverSideSorting
          getRowId={(event) => event.id}
          getRowLink={(row) => {
            if (!row.original.pgaId || !row.original.linkPath) return null;
            return {
              to: row.original.linkPath,
              "aria-label": `View ${row.original.personName}`
            };
          }}
          hasActiveFilters={activeFilters || showEmptyTable}
          renderControls={(_table, { onSortClick }) =>
            isCompactLayout ? (
              <TableControls
                activeFilters={activeFilters ? 1 : 0}
                onClearAllFilters={clearFilters}
                onSortClick={onSortClick}
              >
                {tableFilters}
              </TableControls>
            ) : null
          }
          renderFilteredEmptyState={() =>
            showEmptyTable ? <EmptyRosterActivity /> : <FilteredEmptyState useColumnHeaderFilters={showHeaderFilters} />
          }
          data-testid="roster-activity-results-table"
        />
      </RosterResultsHeaderProvider>

      <PersonSideSheet
        open={!!sideSheet}
        onClose={() => setSideSheet(null)}
        pgaId={sideSheet?.pgaId ?? null}
        showPersonalDetails
        showEmployment
        customActionButtons={
          sideSheet?.linkPath
            ? [
                {
                  text: "View Person",
                  variant: "outlined",
                  testId: "roster-activity-view-person",
                  onClick: () => {
                    const linkPath = sideSheet.linkPath;
                    if (!linkPath) return;
                    navigate(linkPath);
                    setSideSheet(null);
                  }
                }
              ]
            : undefined
        }
      />
    </div>
  );
}
